import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Input } from 'antd';
import { CloseIcon } from '../../assets/icons';
import './ReferralCodeModal.scss';
import { Images } from '../../config/images';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import CopyClipboardControl from '../CopyClipboardControl/CopyClipboardControl';
import { copyTextToClipboard } from '../../utils';
import { useForm, useWatch } from 'antd/es/form/Form';
import { referralCodeFormConstant } from './referralCodeForm.constant';
import { referralCodeRules } from './referralCodeForm.rules';
import { saveReferralCodeApi } from '../../services/referralCodeApi/referralCodeApi';
import { toastNotify } from '../../utils/toastNotify';
import useAlphaNumericInput from '../../Hooks/useAlphaNumericInput';

/**
 * @typedef {object} ReferralCodeModalProps
 * @property {boolean} open
 * @property {string} referralCode
 * @property {string} referralLink
 * @property {()=>{}} refetchReferralLink
 * @property {boolean} showForm
 * @property {()=>void} onClose
 * @property {import("react").ReactNode} description
 * @param {ReferralCodeModalProps} props
 */
const ReferralCodeModal = ({ open, onClose, referralCode, referralLink, showForm, refetchReferralLink }) => {
  const [copied, setCopied] = useState(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localReferralLink, setLocalReferralLink] = useState(referralLink);
  const [localShowForm, setLocalShowForm] = useState(showForm);
  const domain = referralLink.split(referralCode)?.[0];
  const { onKeyDown, onInput, onPaste } = useAlphaNumericInput(15,(sanitizedText)=>{
    form.setFieldValue(referralCodeFormConstant.ReferralCode,sanitizedText);
  });

  useEffect(() => {
    setLocalShowForm(showForm);
  }, [showForm]);

  useEffect(() => {
    setLocalReferralLink(referralLink);
  }, [referralLink]);
  const [form] = useForm();
  const handleCopy = () => {
    copyTextToClipboard(referralLink);
    setCopied(true); // Change icon to CheckCircle after copy
    setTimeout(() => setCopied(false), 10000);
  };
  useEffect(() => {
    return () => {
      setCopied(false);
      form.resetFields([referralCodeFormConstant.ReferralCode]);
    };
  }, []);

  const handleFormChange = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
    setSubmitButtonDisabled(hasErrors);
  };

  const onModalClose = () => {
    setCopied(false);
    form.resetFields();
    onClose();
  };

  const onSave = async () => {
    try {
      await form.validateFields();
      const newReferralCode = form.getFieldValue(referralCodeFormConstant.ReferralCode);
      if (newReferralCode) {
        setIsLoading(true);
        const response = await saveReferralCodeApi({ referralCode: newReferralCode?.trim()?.toUpperCase() });
        if (!response.isError) {
          toastNotify('success', response?.message);
          setLocalReferralLink(response?.data?.referralLink);

          refetchReferralLink();
          setLocalShowForm(false);
        } else {
          toastNotify('error', response?.message);
        }
      }
    } catch (err) {
      toastNotify('error', err?.response?.data?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const watchedReferralCode = useWatch([referralCodeFormConstant.ReferralCode], form);
  const newReferralLink = useMemo(
    () => (showForm && !isLoading && watchedReferralCode ? `${domain}${watchedReferralCode}` : localReferralLink),
    [domain, watchedReferralCode, showForm, localReferralLink]
  );

  /**
   *
   * @param {import('react').KeyboardEvent<HTMLInputElement>} e
   * @description allow only uppercase and numbers
   */

  return (
    <Modal open={open} onCancel={onModalClose} closeIcon={<CloseIcon />} footer={false} width={524} centered>
      <div className=''>
        <div className='mb-16 success__vector success'>
          <LazyLoadImage src={Images.success} alt='success vector art' />
        </div>
        {localShowForm && (
          <Form id='referral-code-form' layout='vertical' form={form} onFieldsChange={handleFormChange} onSubmitCapture={onSave} disabled={isLoading}>
            <Form.Item
              name={referralCodeFormConstant.ReferralCode}
              label='Edit Your Referral Link'
              labelCol={{ style: { textAlign: 'center' } }}
              colon
              validateFirst
              initialValue={referralCode}
              rules={referralCodeRules[referralCodeFormConstant.ReferralCode]}
              className='mb-4'
            >
              <Input
                onInput={onInput}
                placeholder='Your Referral Code'
                className='text-center'
                onKeyDown={onKeyDown}
                style={{ textTransform: 'uppercase' }}
                maxLength={15}
                onPaste={onPaste}
              />
            </Form.Item>
            <p className='  fz-12 align-warning'>Once saved your referral link cannot be changed.</p>
          </Form>
        )}
        <div className='success mt-32'>
          <h4 className='success__title mb-16'>Referral Link</h4>
          <div className='mb-32 text-taupe w-100'>
            <CopyClipboardControl linkText={newReferralLink.toUpperCase()} copied={copied} handleCopy={handleCopy} showCopyBtn={localShowForm} />
          </div>
        </div>
      </div>

      {!localShowForm ? (
        <Button type='primary' className='w-100' onClick={onClose}>
          Done
        </Button>
      ) : (
        <Button form='referral-code-form' type='primary' htmlType='submit' disabled={submitButtonDisabled} loading={isLoading} className='w-100'>
          Save
        </Button>
      )}
    </Modal>
  );
};

export default ReferralCodeModal;
